import externalAxios from "axios";
import qs from "qs";

function isFunction(obj) {
  return !!(obj && obj.constructor && obj.call && obj.apply);
}

class VueAuth {
  token = null;
  refreshToken = null;
  expiresAt = null;
  ready = false;
  checkTokenInterval = null;
  loginUrl = null;
  refreshUrl = null;
  oauthToken = null;

  constructor(
    { loginUrl, refreshUrl, oauthToken, scope, clientId },
    { axios, router: vueRouter }
  ) {
    this.axios = axios || externalAxios;
    this.vueRouter = vueRouter;
    this.loginUrl = loginUrl;
    this.refreshUrl = refreshUrl;
    this.oauthToken = oauthToken;
    this.scope = scope;
    this.scope = scope;
    this.clientId = clientId;

    this.token = localStorage.getItem(this.scope + "VueAuth_token");
    this.refreshToken = localStorage.getItem(
      this.scope + "VueAuth_refreshToken"
    );
    this.expiresAt = localStorage.getItem(this.scope + "VueAuth_expiresAt");

    this.checkTokenInterval = null;

    this.axios.interceptors.request.use(request => {
      if (request.needAuth && this.token) {
        request.headers.Authorization = `Bearer ${this.token}`;
        delete request.needAuth;
      }
      return request;
    });

    this.vueRouter.beforeEach((to, from, next) => {
      if (to.meta && to.meta.auth) {
        let needAuth;
        if (isFunction(to.meta.auth)) {
          needAuth = to.meta.auth();
        } else {
          needAuth = true;
        }

        if (this.token || needAuth === false) {
          next();
        } else {
          next({ name: "auth.login" });
        }
      } else {
        next();
      }
    });
  }

  async start() {
    await this._checkExpiredToken();
    if (this.token) {
      this.checkTokenInterval = setInterval(
        this._checkExpiredToken.bind(this),
        2 * 1000 * 60
      );
    }
    this.ready = true;
  }

  async _checkExpiredToken() {
    if (this.expiresAt) {
      const expiresAt = new Date(this.expiresAt);
      expiresAt.setMinutes(expiresAt.getMinutes() - 10);
      const now = new Date();
      if (now > expiresAt) {
        await this._refresh();
      }
    }
  }

  check() {
    return this.token != null && this.ready;
  }

  // eslint-disable-next-line
  async login({ grant_type = "password", username, password }) {
    const response = await this.axios.post(
      this.loginUrl,
      qs.stringify({
        grant_type,
        scope: this.scope,
        username: `${this.clientId}:${username}`,
        password
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Basic ${this.oauthToken}`
        }
      }
    );
    this.checkTokenInterval = setInterval(
      this._checkExpiredToken,
      2 * 1000 * 60
    );
    localStorage.setItem(
      this.scope + "VueAuth_token",
      response.data.access_token
    );
    this.token = response.data.access_token;
    localStorage.setItem(
      this.scope + "VueAuth_refreshToken",
      response.data.refresh_token
    );
    this.refreshToken = response.data.refresh_token;
    localStorage.setItem(
      this.scope + "VueAuth_expiresAt",
      response.data.expires_at
    );
    this.expiresAt = response.data.expires_at;

    this.ready = true;
    return response;
  }

  logout() {
    this.token = null;
    localStorage.removeItem(this.scope + "VueAuth_token");
    this.refreshToken = null;
    localStorage.removeItem(this.scope + "VueAuth_refreshToken");
    this.expiresAt = null;
    localStorage.removeItem(this.scope + "VueAuth_expiresAt");
    clearInterval(this.checkTokenInterval);
    this.checkTokenInterval = null;
  }

  // eslint-disable-next-line
  async _refresh({ grant_type = "refresh_token" } = {}) {
    try {
      const response = await this.axios.post(
        this.refreshUrl,
        qs.stringify({
          grant_type,
          refresh_token: this.refreshToken
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Basic ${this.oauthToken}`
          }
        }
      );
      localStorage.setItem(
        this.scope + "VueAuth_token",
        response.data.access_token
      );
      this.token = response.data.access_token;
      localStorage.setItem(
        this.scope + "VueAuth_refreshToken",
        response.data.refresh_token
      );
      this.refreshToken = response.data.refresh_token;
      localStorage.setItem(
        this.scope + "VueAuth_expiresAt",
        response.data.expires_at
      );
      this.expiresAt = response.data.expires_at;
    } catch (error) {
      this.logout();
    }
  }
}

export default VueAuth;
